import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { useAuth } from '../context/AuthContext';
import { Dialog } from '../components/prompts/PromptDialogs';
import './AiPrompts.css';

const PasswordStep = ({ onContinue, busy, error, onClose }) => {
  const [password, setPassword] = useState('');
  return (
    <Dialog
      closeOnEscape
      title="Turn on two-factor sign-in"
      description="Confirm your password to start."
      onClose={onClose}
      footer={(
        <>
          {error && <p className="pe-dialog-error" role="alert">{error}</p>}
          <div className="pe-spacer" />
          <button type="button" className="pe-button" onClick={onClose}>Cancel</button>
          <button type="button" className="pe-button is-primary" disabled={busy || !password} onClick={() => onContinue(password)}>Continue</button>
        </>
      )}
    >
      <label className="pe-field is-first">
        <span className="pe-field-label">Password</span>
        <input className="pe-input" type="password" autoComplete="current-password" autoFocus value={password} onChange={(event) => setPassword(event.target.value)} />
      </label>
    </Dialog>
  );
};

const ScanStep = ({ setup, onConfirm, busy, error, onClose }) => {
  const [qrCode, setQrCode] = useState(null);
  const [code, setCode] = useState('');

  useEffect(() => {
    let isCurrent = true;
    QRCode.toDataURL(setup.otpauthUrl, { width: 220, margin: 1 }).then((url) => {
      if (isCurrent) {
        setQrCode(url);
      }
    });
    return () => {
      isCurrent = false;
    };
  }, [setup.otpauthUrl]);

  return (
    <Dialog
      title="Scan this code"
      description="Open your authenticator app, such as Google Authenticator or 1Password, and add an account with this code. Then enter the 6 digits it shows."
      onClose={onClose}
      footer={(
        <>
          {error && <p className="pe-dialog-error" role="alert">{error}</p>}
          <div className="pe-spacer" />
          <button type="button" className="pe-button" onClick={onClose}>Cancel</button>
          <button type="button" className="pe-button is-primary" disabled={busy || code.trim().length < 6} onClick={() => onConfirm(code)}>Turn on</button>
        </>
      )}
    >
      <div className="pe-qr">
        {qrCode && <img src={qrCode} width="220" height="220" alt="QR code for your authenticator app" />}
      </div>
      <p className="pe-muted pe-center">Cannot scan it? Enter this key in the app:</p>
      <p className="pe-secret">{setup.secret}</p>
      <label className="pe-field">
        <span className="pe-field-label">6-digit code</span>
        <input className="pe-input" inputMode="numeric" autoComplete="one-time-code" autoFocus placeholder="123456" value={code} onChange={(event) => setCode(event.target.value)} />
      </label>
    </Dialog>
  );
};

const RecoveryCodesStep = ({ codes, onDone }) => {
  const [isSaved, setIsSaved] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(codes.join('\n'));
    setIsCopied(true);
  };

  return (
    <Dialog
      title="Save your recovery codes"
      description="Two-factor sign-in is on. If you lose your phone, each of these codes signs you in once. They are shown only now, so keep them somewhere safe, away from your phone."
      onClose={() => {}}
      hideClose
      footer={(
        <>
          <label className="pe-check is-small">
            <input type="checkbox" checked={isSaved} onChange={(event) => setIsSaved(event.target.checked)} />
            <span>I saved these codes</span>
          </label>
          <div className="pe-spacer" />
          <button type="button" className="pe-button" onClick={copy}>{isCopied ? 'Copied' : 'Copy'}</button>
          <button type="button" className="pe-button is-primary" disabled={!isSaved} onClick={onDone}>Done</button>
        </>
      )}
    >
      <ul className="pe-recovery-codes">
        {codes.map((code) => <li key={code}>{code}</li>)}
      </ul>
    </Dialog>
  );
};

const TurnOffStep = ({ onTurnOff, busy, error, onClose }) => {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  return (
    <Dialog
      closeOnEscape
      title="Turn off two-factor sign-in?"
      description="Signing in will need only the password again, and changing OpenAI keys will not ask for a code."
      onClose={onClose}
      footer={(
        <>
          {error && <p className="pe-dialog-error" role="alert">{error}</p>}
          <div className="pe-spacer" />
          <button type="button" className="pe-button" onClick={onClose}>Cancel</button>
          <button type="button" className="pe-button is-danger" disabled={busy || !password || !code.trim()} onClick={() => onTurnOff(password, code)}>Turn off</button>
        </>
      )}
    >
      <label className="pe-field is-first">
        <span className="pe-field-label">Password</span>
        <input className="pe-input" type="password" autoComplete="current-password" autoFocus value={password} onChange={(event) => setPassword(event.target.value)} />
      </label>
      <label className="pe-field">
        <span className="pe-field-label">Code from your authenticator app, or a recovery code</span>
        <input className="pe-input" autoComplete="one-time-code" value={code} onChange={(event) => setCode(event.target.value)} />
      </label>
    </Dialog>
  );
};

const Settings = () => {
  const { fetchWithAuth, login } = useAuth();
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [step, setStep] = useState(null);
  const [setup, setSetup] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const call = async (path, options) => {
    const response = await fetchWithAuth(path, options);
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(data?.error || `Request failed (${response.status})`);
    }
    return data;
  };

  const post = (path, body) => call(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  // Not memoized: turning two-factor on replaces the token, and an older fetchWithAuth still
  // sends the revoked one, which signs the admin out.
  const loadStatus = async () => {
    setStatus(await call('/api/admin/security/two-factor'));
  };

  useEffect(() => {
    loadStatus().catch((failure) => setLoadError(failure.message));
    // Only on opening the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (action) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (failure) {
      setError(failure.message);
    } finally {
      setBusy(false);
    }
  };

  const open = (next) => {
    setError(null);
    setStep(next);
  };

  const startSetup = (password) => run(async () => {
    setSetup(await post('/api/admin/security/two-factor/setup', { password }));
    setStep('scan');
  });

  const confirmSetup = (code) => run(async () => {
    const result = await post('/api/admin/security/two-factor/confirm', { code });
    // Turning it on ends older sessions, so this one continues with the new token.
    login(result.token, result.user);
    setRecoveryCodes(result.recoveryCodes);
    setSetup(null);
    setStep('codes');
  });

  const turnOff = (password, code) => run(async () => {
    await post('/api/admin/security/two-factor/disable', { password, code });
    setStep(null);
    await loadStatus();
  });

  const finishCodes = () => {
    setRecoveryCodes(null);
    setStep(null);
    loadStatus().catch((failure) => setLoadError(failure.message));
  };

  return (
    <div className="prompt-page">
      <header className="pe-header">
        <div className="pe-title"><h1 className="pe-list-title">Settings</h1></div>
      </header>

      <div className="pe-list">
        {loadError && <div className="pe-alert is-inline" role="alert">{loadError}</div>}

        {status && (
          <section className="pe-card" aria-label="Two-factor sign-in">
            <div className="pe-card-head">
              <div>
                <h2>Two-factor sign-in {status.enabled ? <span className="pe-badge">On</span> : <span className="pe-badge is-off">Off</span>}</h2>
                <p className="pe-muted">
                  {status.enabled
                    ? `On since ${new Date(status.enabledAt).toLocaleDateString()}. Signing in and changing OpenAI keys ask for a code from your authenticator app. ${status.recoveryCodesLeft} recovery ${status.recoveryCodesLeft === 1 ? 'code' : 'codes'} left.`
                    : 'Signing in will also ask for a 6-digit code from an authenticator app on your phone, so a stolen password is not enough.'}
                </p>
              </div>
              {status.enabled
                ? <button type="button" className="pe-button" onClick={() => open('off')}>Turn off</button>
                : <button type="button" className="pe-button is-primary" disabled={!status.available} onClick={() => open('password')}>Turn on</button>}
            </div>
            {!status.available && !status.enabled && (
              <p className="pe-warning pe-card-message">This server needs SECRETS_ENCRYPTION_KEY and JWT_SECRET before two-factor sign-in can be turned on.</p>
            )}
            {status.enabled && status.recoveryCodesLeft <= 2 && (
              <p className="pe-warning pe-card-message">Few recovery codes are left. Turn two-factor sign-in off and on again to get new ones.</p>
            )}
          </section>
        )}
      </div>

      {step === 'password' && <PasswordStep busy={busy} error={error} onContinue={startSetup} onClose={() => setStep(null)} />}
      {step === 'scan' && setup && <ScanStep setup={setup} busy={busy} error={error} onConfirm={confirmSetup} onClose={() => setStep(null)} />}
      {step === 'codes' && recoveryCodes && <RecoveryCodesStep codes={recoveryCodes} onDone={finishCodes} />}
      {step === 'off' && <TurnOffStep busy={busy} error={error} onTurnOff={turnOff} onClose={() => setStep(null)} />}
    </div>
  );
};

export default Settings;
